// ─────────────────────────────────────────────────────────────────────────────
// tool_registry.ts — Per-tool state with persistence
//
// Replaces the global `_penStyle` model where every tool shared one
// colour / size / opacity object. Each tool now owns its own params;
// switching tools loads them, modifying them writes to *that* tool,
// not globally. Persists alongside settings.
//
// What's a Tool today vs. a Brush:
//
//   - Tool   = user-facing concept. Has an id ('ink', 'pencil', …),
//              a display name + icon, a `brushKind` selecting which
//              renderer paints its strokes, and `params` (color, size,
//              opacity) the user customises freely.
//
//   - Brush  = renderer (lives in ink_layer.ts). Stateless function
//              that consumes a stroke + a tool's params and produces
//              pixels. Multiple tools can share a brushKind (e.g.
//              'ink', 'marker', 'infill' all use the 'solid' brush
//              today, just with different default profiles).
//
// Batch 4 only formalises Tool ownership of params + persistence.
// Batch 5 will add the kinematic-aware brushes.
// ─────────────────────────────────────────────────────────────────────────────

import type { PenStyle, BrushKind } from '@shared/notebook_model';
import { DEFAULT_INK_STYLE } from '@shared/notebook_model';

// BrushKind is defined in shared/notebook_model.ts and referenced from
// both this registry and the InkLayer's brush dispatch — keeping them
// in sync via a single import point.
export type { BrushKind };

/** User-customisable parameters per tool. The values the stylus
 *  menu's swatches / sliders write to. */
export interface ToolUserParams {
  color: string;
  size: number;
  opacity: number;
}

/** A registered tool. The catalogue's `defaults` are the factory
 *  preset; `params` is what the user has customised (initially a
 *  copy of `defaults`). */
export interface Tool {
  id: PenStyle;
  name: string;
  icon: string;
  brushKind: BrushKind;
  defaults: ToolUserParams;
  params: ToolUserParams;
}

/** The factory catalogue. Each entry seeds the registry the first
 *  time the app boots. Add a new tool here + a matching Brush in
 *  ink_layer.ts (Batch 5+) and it's selectable from the stylus menu. */
const TOOL_CATALOGUE: ReadonlyArray<{
  id: PenStyle;
  name: string;
  icon: string;
  brushKind: BrushKind;
  defaults: ToolUserParams;
}> = [
  { id: 'ink',        name: 'Ink',        icon: '◆',  brushKind: 'solid',
    defaults: { color: DEFAULT_INK_STYLE.color, size: 3,   opacity: 0.92 } },
  { id: 'pencil',     name: 'Pencil',     icon: '✎',  brushKind: 'textured',
    defaults: { color: '#3a2010', size: 2.2, opacity: 0.55 } },
  { id: 'graphite',   name: 'Graphite',   icon: '✐',  brushKind: 'textured',
    defaults: { color: '#202020', size: 2.6, opacity: 0.40 } },
  { id: 'marker',     name: 'Marker',     icon: '▮',  brushKind: 'solid',
    defaults: { color: '#cc4020', size: 6,   opacity: 0.85 } },
  { id: 'chalk',      name: 'Chalk',      icon: '◈',  brushKind: 'textured',
    defaults: { color: '#e8d4ae', size: 5,   opacity: 0.50 } },
  { id: 'watercolor', name: 'Watercolour', icon: '◉', brushKind: 'wet',
    defaults: { color: '#3a6f8a', size: 8,   opacity: 0.25 } },
  { id: 'infill',     name: 'Infill',     icon: '■',  brushKind: 'solid',
    defaults: { color: '#5a3820', size: 12,  opacity: 0.70 } },
  { id: 'nib',        name: 'Nib',        icon: '◢',  brushKind: 'stylus',
    defaults: { color: '#1a1a1a', size: 4,   opacity: 0.95 } },
  // Eraser uses the solid brush with destination-out composite —
  // no separate brushKind, the BrushProfile carries the composite op.
  // `size` is the eraser radius in page pixels; `opacity` is the
  // edge-smoothing knob (1 = hard cut, 0 = soft gradient only).
  { id: 'eraser',     name: 'Eraser',     icon: '⌫',  brushKind: 'solid',
    defaults: { color: '#000000', size: 14,  opacity: 1.00 } },
];

/** Per-tool params as serialised. Only what the user changes — the
 *  catalogue (id, brushKind, name, icon, defaults) is code, not
 *  persisted. */
export type SerialisedTools = Partial<Record<PenStyle, ToolUserParams>>;

/** Owns the catalogue + active tool selection + per-tool param
 *  state. Notifies via `onChange` so the canvas can refresh
 *  derived state (e.g. `_penStyle`) any time the active tool's
 *  params shift. */
export class ToolRegistry {
  private readonly _tools = new Map<PenStyle, Tool>();
  private _activeId: PenStyle = 'ink';

  /** Fires whenever the active tool changes OR the active tool's
   *  params are updated. Lets the canvas keep its derived
   *  `_penStyle` in sync. */
  onChange: ((tool: Tool) => void) | null = null;

  constructor() {
    for (const def of TOOL_CATALOGUE) {
      this._tools.set(def.id, {
        id: def.id,
        name: def.name,
        icon: def.icon,
        brushKind: def.brushKind,
        defaults: { ...def.defaults },
        params: { ...def.defaults },
      });
    }
  }

  // ── Reads ──────────────────────────────────────────────────────────────

  get activeId(): PenStyle { return this._activeId; }
  active(): Tool { return this._tools.get(this._activeId)!; }
  get(id: PenStyle): Tool | undefined { return this._tools.get(id); }
  all(): Tool[] { return [...this._tools.values()]; }

  // ── Mutations ──────────────────────────────────────────────────────────

  /** Switch the active tool. No-op if the id is unknown or already
   *  active. Fires onChange with the new active tool. */
  pick(id: PenStyle): Tool | null {
    const t = this._tools.get(id);
    if (!t) return null;
    if (this._activeId === id) return t;
    this._activeId = id;
    this.onChange?.(t);
    return t;
  }

  /** Apply a partial param update to the active tool. Keys that are
   *  undefined in `patch` are left untouched. Fires onChange. */
  updateActiveParams(patch: Partial<ToolUserParams>): void {
    const t = this.active();
    if (patch.color !== undefined)   t.params.color = patch.color;
    if (patch.size !== undefined)    t.params.size = patch.size;
    if (patch.opacity !== undefined) t.params.opacity = patch.opacity;
    this.onChange?.(t);
  }

  // ── Persistence ────────────────────────────────────────────────────────

  /** Snapshot for `settings.tools` — only stores per-tool params,
   *  not catalogue metadata. Active id rides along under a sentinel
   *  key so reload restores both. */
  serialize(): { active: PenStyle; params: SerialisedTools } {
    const params: SerialisedTools = {};
    for (const [id, tool] of this._tools) {
      params[id] = { ...tool.params };
    }
    return { active: this._activeId, params };
  }

  /** Restore from a previous `serialize()`. Tolerates missing tools
   *  (catalogue extension) and unknown ids (catalogue shrinkage). */
  hydrate(saved: { active?: PenStyle; params?: SerialisedTools } | undefined): void {
    if (!saved) return;
    if (saved.params) {
      for (const [id, params] of Object.entries(saved.params) as Array<[PenStyle, ToolUserParams | undefined]>) {
        const tool = this._tools.get(id);
        if (!tool || !params) continue;
        if (typeof params.color === 'string')   tool.params.color = params.color;
        if (typeof params.size === 'number')    tool.params.size = params.size;
        if (typeof params.opacity === 'number') tool.params.opacity = params.opacity;
      }
    }
    if (saved.active && this._tools.has(saved.active)) {
      this._activeId = saved.active;
    }
  }
}
